import { ChatCard } from './types';
import UserMessageCard from './cards/UserMessageCard';
import SystemMessageCard from './cards/SystemMessageCard';
import ApplicantCard from './cards/ApplicantCard';
import ReportCard from './cards/ReportCard';
import NoReportCard from './cards/NoReportCard';
import AgentThinkingCard from './cards/AgentThinkingCard';
import AnalysisCard from './cards/AnalysisCard';
import AgentErrorCard from './cards/AgentErrorCard';
import ErrorCard from './cards/ErrorCard';
import OwnerPickerCard from './cards/OwnerPickerCard';

interface Props {
  card: ChatCard;
  isLast?: boolean;
  onDismissNoReport?: (cardId: string) => void;
  onRetryNoReport?: (cardId: string, applicationNumber: number, ownerIndex: number) => void;
  onPickOwner?: (cardId: string, applicationNumber: number, ownerIndex: number) => void;
  onDismissError?: (cardId: string) => void;
  onRetryCommand?: (cardId: string, command: string) => void;
  onRetry?: (cardId: string, lastUserMessage: string) => void;
}

const ChatCardRouter = ({
  card,
  isLast,
  onDismissNoReport,
  onRetryNoReport,
  onPickOwner,
  onDismissError,
  onRetryCommand,
  onRetry,
}: Props) => {
  switch (card.type) {
    case 'user_message':
      return <UserMessageCard text={card.text} />;
    case 'system_message':
      return <SystemMessageCard text={card.text} spinner={!!card.spinner && !!isLast} />;
    case 'applicant_card':
      return <ApplicantCard data={card.data} />;
    case 'report_card':
      return <ReportCard data={card.data} />;
    case 'no_report_card':
      return (
        <NoReportCard
          applicationId={card.applicationId}
          applicationNumber={card.applicationNumber}
          ownerIndex={card.ownerIndex}
          ownerName={card.ownerName}
          state={card.state}
          missingFields={card.missingFields}
          resolved={card.resolved}
          onRetry={() =>
            card.applicationNumber !== undefined &&
            onRetryNoReport?.(card.id, card.applicationNumber, card.ownerIndex)
          }
          onDismiss={() => onDismissNoReport?.(card.id)}
        />
      );
    case 'owner_picker_card':
      return (
        <OwnerPickerCard
          applicationId={card.applicationId}
          applicationNumber={card.applicationNumber}
          rawNeedle={card.rawNeedle}
          candidates={card.candidates}
          reason={card.reason}
          resolved={card.resolved}
          onPick={(ownerIndex) => onPickOwner?.(card.id, card.applicationNumber, ownerIndex)}
        />
      );
    case 'agent_thinking':
      return <AgentThinkingCard />;
    case 'analysis_card':
      return (
        <AnalysisCard
          text={card.text}
          widgets={card.widgets}
          segments={card.segments}
          isStreaming={card.isStreaming}
          error={card.error}
          messageId={card.messageId}
          sessionId={card.sessionId}
        />
      );
    case 'agent_error':
      return (
        <AgentErrorCard
          error={card.error}
          code={card.code}
          retryable={card.retryable}
          canRetry={!!card.lastUserMessage}
          onRetry={card.lastUserMessage ? () => onRetry?.(card.id, card.lastUserMessage!) : undefined}
        />
      );
    case 'error_card':
      return (
        <ErrorCard
          text={card.text}
          severity={card.severity}
          applicationId={card.applicationId}
          applicationNumber={card.applicationNumber}
          actions={card.actions}
          resolved={card.resolved}
          onRetry={(command) => onRetryCommand?.(card.id, command)}
          onDismiss={() => onDismissError?.(card.id)}
        />
      );
    default: {
      const _exhaustive: never = card;
      return null;
    }
  }
};

export default ChatCardRouter;
